import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { insertTrackEvent } from '@comeon/analytics';

import { selectPlayerId } from '~/components/account/ducks';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import useRefState from '~/hooks/use-ref-state';
import { selectFranchiseId } from '~/components/brand/ducks/selectors';
import useConfiguration from '~/configuration/use-configuration';
import { loadScript } from '~/utils/globals';

import { getComplianceResult, isComplianceExpired, updateDeniedStatus } from './api';
import { CONFIG, EVENTS, States, eventStream } from './config';
import { reportException } from '@comeon/error-reporter';
import { ErrorReporterDomain } from '../../error-handling/error-reporter-domain';

const useLocationSmart = () => {
	const authenticated = useSelector(authenticationSelector);
	const franchiseId = useSelector(selectFranchiseId);
	const playerId = useSelector(selectPlayerId);

	const [profileSessionId, setProfileSessionId] = useRefState(null);
	/**
	 * States to hold all the states of locationsmart
	 */
	const [currentState, setCurrentState] = useRefState(States.start);
	const [timer, setTimer] = useRefState(null);

	const { lsblUrl, lsdpDomain, lsOrgID, lsblDomain } = useConfiguration();

	/**
	 * Emit updates to the event Stream
	 */
	const emitUpdates = useCallback((status, data = {}) => {
		eventStream.emit(EVENTS.update, { status, data });
	}, []);

	const profile = useCallback(async () => {
		if (currentState.current === States.pending || !playerId) {
			return;
		}

		setCurrentState(States.pending);
		emitUpdates(States.pending);

		const response = await isComplianceExpired({
			sessionId: playerId
		});

		if (!response.value) {
			// Location smart cache response for compliance is still valid
			insertTrackEvent({
				category: 'Location Smart',
				action: 'Location Smart cache still valid',
				label: `${franchiseId} - ${playerId}`
			});
			setCurrentState(States.complete);
			return emitUpdates(States.complete);
		}

		const pSessionId = `${playerId}${Date.now()}`;
		setProfileSessionId(pSessionId);

		/**
		 * Call location smart device profiling
		 */
		if (window.lsdp) {
			const domain = lsdpDomain || CONFIG.profilingDomain;
			const orgID = lsOrgID || CONFIG.orgID;
			window.lsdp.dp(domain, orgID, pSessionId);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentState, lsOrgID, lsdpDomain, playerId]);

	const handleFailure = useCallback(
		denied => {
			emitUpdates(States.complete, { error: true, denied });
			clearTimeout(timer.current);

			if (denied) {
				return insertTrackEvent({
					category: 'Location Smart',
					action: 'Denied',
					label: `${franchiseId} - ${playerId}`
				});
			}

			insertTrackEvent({
				category: 'Location Smart',
				action: 'Failed',
				label: `${franchiseId} - ${playerId}`
			});
		},
		[emitUpdates, franchiseId, playerId, timer]
	);

	const verifyComplianceResult = useCallback(
		async ip => {
			try {
				const response = await getComplianceResult({
					html5SessionId: playerId,
					profileSessionId: profileSessionId.current ?? playerId,
					ipAddress: ip
				});

				setCurrentState(States.complete);

				if (!response?.value) {
					return handleFailure();
				}

				emitUpdates(States.complete);
			} catch (e) {
				setCurrentState(States.complete);
				emitUpdates(States.complete, { error: true });

				reportException(e, {
					domain: ErrorReporterDomain.AUTHENTICATION,
					type: 'location-smart-error',
					info: profileSessionId.current,
					url: window.location
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[handleFailure, playerId, profileSessionId]
	);

	const checkCallback = useCallback(
		response => {
			/**
			 * If the current state is not immediate step after the html5 check
			 * or if the check is already in processing state
			 * then return
			 */
			if (currentState.current !== States.resolving || currentState.current === States.processing) {
				return;
			}

			setCurrentState(States.processing);

			const code = response?.locationData?.code;
			const message = response?.locationData?.message;

			if (code === '0') {
				insertTrackEvent({
					category: 'Location Smart',
					action: 'Successful',
					label: `${franchiseId} - ${playerId}`
				});

				return verifyComplianceResult(response?.locationData?.submitterIpAddress);
			}

			// eslint-disable-next-line no-console
			console.log(response?.locationData?.message ?? 'Location Request Failed');
			/**
			 * use location data code 1 to detect denied location. LS return code 1 when user location do not match in subsequent requests
			 */
			handleFailure(code === '1' && message !== 'The permission was revoked');

			try {
				updateDeniedStatus({ html5SessionId: playerId });
			} catch (_) {
				// eslint-disable-next-line no-console
				console.warn('Failed to save the denied status');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[franchiseId, handleFailure, playerId, verifyComplianceResult]
	);

	const check = useCallback(async () => {
		if (!playerId) {
			return;
		}

		const lsdomain = lsblDomain && lsblDomain !== '' ? lsblDomain : CONFIG.customerDomain;

		window.lshtml5(
			playerId,
			CONFIG.enableHighAccuracy,
			CONFIG.maxAge,
			CONFIG.timeout,
			checkCallback,
			lsdomain,
			CONFIG.watch,
			CONFIG.watchDuration
		);

		setCurrentState(States.resolving);

		insertTrackEvent({
			category: 'Location Smart',
			action: 'Location check started',
			label: `${franchiseId} - ${playerId}`
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkCallback, franchiseId, lsblDomain, playerId]);

	const setup = useCallback(async () => {
		if (currentState.current !== States.start) {
			return;
		}

		setCurrentState(States.initialized);

		const scriptUrl = lsblUrl !== '' ? lsblUrl : CONFIG.scriptURL;
		await loadScript(scriptUrl, {
			'data-name': 'location-smart'
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authenticated, currentState, lsblUrl]);

	const periodicCheck = useCallback(() => {
		const timerId = setTimeout(() => {
			profile();
			periodicCheck();
		}, CONFIG.periodicLookupDelay);

		setTimer(timerId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profile]);

	const onDeviceProfiling = useCallback(
		({ id }) => {
			if (currentState.current !== States.pending && id !== profileSessionId.current) {
				return;
			}

			check();
		},
		[check, currentState, profileSessionId]
	);

	const initialize = useCallback(
		(periodic = true) => {
			setCurrentState(States.init);
			profile();

			if (periodic) {
				periodicCheck();
			}

			/**
			 * Off and then add to avoid multiple callbacks for single event
			 */
			eventStream.off(EVENTS.profile_done, onDeviceProfiling).on(EVENTS.profile_done, onDeviceProfiling);
		},
		[onDeviceProfiling, periodicCheck, profile, setCurrentState]
	);

	const onStop = useCallback(() => {
		setCurrentState(States.stop);
		clearTimeout(timer.current);
		eventStream.off(EVENTS.profile_done, onDeviceProfiling);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onDeviceProfiling, timer]);

	useEffect(() => {
		eventStream.on(EVENTS.init, initialize);
		eventStream.on(EVENTS.stop, onStop);

		return () => {
			eventStream.off(EVENTS.init, initialize);
			eventStream.off(EVENTS.stop, onStop);
		};
	}, [initialize, onStop]);

	useEffect(() => {
		setup();
	}, [setup]);

	return null;
};

export default useLocationSmart;
